/**
 * MONSTER TOWN — PANEL HOST. HUD/AUDIO/TOUCH CREW (src/ui/**).
 *
 * Full-screen picture panels (shop, monster creator, slot picker, photo).
 *
 * THE RULE THAT SHAPES THIS FILE: **no dead ends.** Every panel is built through
 * `panelShell()`, which always puts a 112 px back button in the top-left corner
 * before the panel's own content exists. There is no code path that produces a
 * panel a 3-year-old cannot get out of, because the way out is created first.
 *
 * Only one panel is open at a time, and opening one closes the other — a kid can
 * never bury themselves two layers deep.
 */

import { bus } from '../core/events'
import { button, clear, el } from './dom'
import { iconEl } from './icons'

export interface PanelParts {
  root: HTMLElement
  head: HTMLElement
  body: HTMLElement
  foot: HTMLElement
}

export interface PanelShellOpts {
  /** Big picture that says what this panel is. LAW #4 — no title text. */
  icon: string
  onBack: () => void
  /** Extra controls in the header, right of the title. */
  headExtra?: HTMLElement[]
}

/** Builds panel chrome. The way OUT is created before the way in. */
export function panelShell(o: PanelShellOpts): PanelParts {
  const root = el('div', 'mt-panel')
  const head = el('div', 'mt-panel-head')
  const back = button({ icon: 'back', label: 'Back', className: 'big paper', cue: 'ui_back' }, o.onBack)
  const title = el('div', 'mt-panel-title')
  title.append(iconEl(o.icon, 64))
  head.append(back, title)
  for (const extra of o.headExtra ?? []) head.append(extra)
  const body = el('div', 'mt-panel-body')
  const foot = el('div', 'mt-panel-foot')
  root.append(head, body, foot)
  return { root, head, body, foot }
}

export type PanelBuilder = () => PanelParts

export class PanelHost {
  private readonly layer: HTMLElement
  private readonly builders = new Map<string, PanelBuilder>()
  private currentName: string | null = null
  private currentNode: HTMLElement | null = null

  constructor(parent: HTMLElement) {
    this.layer = el('div', 'mt-panel-layer')
    this.layer.style.position = 'absolute'
    this.layer.style.inset = '0'
    this.layer.style.pointerEvents = 'none'
    // A panel must cover the HUD chrome no matter what order the HUD appended
    // its widgets in. Without this the coin purse and the left stack paint on
    // top of a full-screen panel — caught in a captured frame, not by reading
    // the code. (The portrait overlay sits above panels at z-index 40.)
    this.layer.style.zIndex = '30'
    parent.append(this.layer)
  }

  register(name: string, build: PanelBuilder): void {
    this.builders.set(name, build)
  }

  get open(): string | null {
    return this.currentName
  }

  /** Opening a panel closes whatever was open. Never two layers deep. */
  show(name: string): boolean {
    const build = this.builders.get(name)
    if (build === undefined) return false
    if (this.currentName === name) return true
    this.hide(true)
    // CLAIMED BEFORE IT IS BUILT. A builder may legitimately set the app phase
    // (photo mode does), the HUD turns a phase change back into `show()`, and
    // with the name claimed only afterwards that call did not recognise itself
    // as already in progress: it re-entered, built the panel a second time and
    // left two of them stacked in the layer. Claiming first makes a re-entrant
    // show() the no-op it was always meant to be.
    this.currentName = name
    const parts = build()
    this.layer.append(parts.root)
    this.currentNode = parts.root
    bus.emit('ui:panel', { panel: name })
    bus.emit('audio:cue', { cue: 'ui_open' })
    return true
  }

  hide(silent = false): void {
    if (this.currentNode !== null) this.currentNode.remove()
    clear(this.layer)
    const had = this.currentName
    this.currentName = null
    this.currentNode = null
    if (had !== null && !silent) {
      bus.emit('ui:panel', { panel: null })
      bus.emit('audio:cue', { cue: 'ui_close' })
    }
  }

  toggle(name: string): void {
    if (this.currentName === name) this.hide()
    else this.show(name)
  }
}
